import { bridge, isAndroid, isWeb } from 'shared/src/utils';
import { Flex, StackScreen, useSafeArea, useScreenSize } from '@banhala/ui-kit';
import { useHandleBackPressed } from 'shared/src/hooks';
import { useSetScreenName } from 'mypage/src/pages/AlarmSetting/hooks/useSetScreenName';
import { useLogViewEvent } from 'mypage/src/pages/AlarmSetting/hooks/useLogViewEvent';
import { SearchIcon, CartIcon } from 'shared/src/component';
import { ActivityIndicator, ScrollView } from 'react-native';
import type { AlarmSettingSection } from 'shared/src/api/myPageService/getAlarmSettings/types';
import DeviceNotificationGuide from './components/DeviceNotificationGuide/DeviceNotificationGuide';
import Section from './components/Section/Section';
import { useMarketingAlertConfirm } from './hooks/useMarketingAlertConfirm';
import { useNotificationPermission } from './hooks/useNotificationPermission';
import { useMarketingAlarmSettings } from './queries/useMarketingAlarmSettings';
import { useAlarmSettings } from './queries/useAlarmSettings';
import { useToggleAlarmSetting } from './queries/useToggleAlarmSetting';
import { useToggleMarketingAlarmSetting } from './queries/useToggleMarketingAlarmSetting';
import { Suspense } from 'react';

const AlarmSetting = () => {
  const { width } = useScreenSize();
  const { bottom } = useSafeArea();
  const { showBackButton, onPressBack } = useHandleBackPressed();

  const { data: marketingAlarmSetting } = useMarketingAlarmSettings();
  const { data: alarmSettingsData } = useAlarmSettings();

  useSetScreenName();
  useLogViewEvent();

  const marketingSection: AlarmSettingSection = {
    title: '혜택 및 이벤트 알림',
    description: '할인, 쿠폰 등의 이벤트 정보를 가장 먼저 알려드려요',
    alarm_list: [
      {
        title: '문자 알림',
        description: null,
        push_notification_code: 'SMS',
        is_on: marketingAlarmSetting?.sms_agreed_at != null,
      },
      {
        title: '푸시 알림',
        description: null,
        push_notification_code: 'PUSH',
        is_on: marketingAlarmSetting?.push_agreed_at != null,
      },
      {
        title: '야간 푸시 알림(21 ~ 08시)',
        description: null,
        push_notification_code: 'NIGHT_PUSH',
        is_on: marketingAlarmSetting?.night_push_agreed_at != null,
      },
    ],
  };

  const toggleMarketingMutation = useToggleMarketingAlarmSetting();
  const toggleAlarmMutation = useToggleAlarmSetting();
  const confirmMarketingAlert = useMarketingAlertConfirm();

  const toggleSwitch = async (sectionIndex: number, itemIndex: number) => {
    if (sectionIndex === -1) {
      type MarketingType = 'SMS' | 'PUSH' | 'NIGHT_PUSH';
      const item = marketingSection.alarm_list[itemIndex];
      const type = item.push_notification_code as MarketingType;
      const isAgreed = item.is_on;

      if (isAgreed) {
        confirmMarketingAlert(type, () => {
          toggleMarketingMutation.mutate({ type, isAgreed });
        });
      } else {
        toggleMarketingMutation.mutate({ type, isAgreed });
      }
      return;
    }

    const alarm =
      alarmSettingsData.item_list[sectionIndex].alarm_list[itemIndex];

    toggleAlarmMutation.mutate({
      sectionIndex,
      itemIndex,
      isAgreed: alarm.is_on,
      code: alarm.push_notification_code,
    });
  };

  const { isNotificationEnabled, openDeviceNotificationSettings } =
    useNotificationPermission();

  return (
    <StackScreen
      title="알림 설정"
      hideBackButton={!showBackButton}
      onPressBack={onPressBack}
      rightIcons={[
        <SearchIcon screen="ALARM_SETTING" />,
        <CartIcon
          onPressCart={() => {
            bridge.logAnalyticsEvent({
              channel: ['firebase', 'facebook', 'amplitude', 'ablylog'],
              name: 'CLICK_CART',
              analytics: {
                SCREEN: 'ALARM_SETTING',
                LANDING_FROM: 'CARTICON',
              },
            });
          }}
        />,
      ]}
    >
      <Suspense
        fallback={
          <Flex flex={1} width={width} margin={{ horizontal: 'auto' }}>
            <Flex.Center flex={1}>
              <ActivityIndicator />
            </Flex.Center>
          </Flex>
        }
      >
        <ScrollView
          contentContainerStyle={{
            width,
            marginHorizontal: 'auto',
            top: 16,
            paddingHorizontal: 16,
          }}
          showsVerticalScrollIndicator={false}
        >
          {!isNotificationEnabled &&
            !isWeb && ( // 웹은 OS 설정 화면 이동 불가
              <DeviceNotificationGuide
                onPress={openDeviceNotificationSettings}
              />
            )}
          {marketingAlarmSetting && (
            <Section
              section={marketingSection}
              sectionIndex={-1} // 구분용
              onToggle={toggleSwitch}
              isLastSection={false}
            />
          )}
          {alarmSettingsData?.item_list.map((section, sectionIndex) => (
            <Section
              key={section.title}
              section={section}
              sectionIndex={sectionIndex}
              onToggle={toggleSwitch}
              isLastSection={
                sectionIndex === alarmSettingsData?.item_list.length - 1
              }
            />
          ))}
          {isAndroid && <Flex height={bottom} />}
        </ScrollView>
      </Suspense>
    </StackScreen>
  );
};

export default AlarmSetting;
